import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Segmented, theme, Tooltip } from 'antd';
import { Point } from 'ol/geom';
import Polygon from 'ol/geom/Polygon';
import Draw from 'ol/interaction/Draw';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import { IoCloseCircleOutline, IoSparklesOutline } from 'react-icons/io5';
import styled from 'styled-components';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useAuth } from '@contexts/User.context';
import {
	findParse,
	findRoute,
	getFindModels,
	getFindObjects,
	MAX_SEARCH_TILES,
} from '@utils/api/find';
import { capitalizeFirst } from '@utils/helpers';
import { SIMILARITY_Z_INDEX } from '@utils/map/similarity/similaritySource';
import { withAlpha } from '@utils/map/similarity/similarityStyle';

import AnnotationCrosshair from '../../ai/AnnotationCrosshair';
import { CollectionAsk, FindCollections, useViewCollection } from './FindCollections';
import { collectionStep } from './collectionSteps';
import { FindButton, LookingFor } from './FindControls';
import FindPanel, { LAYOUT as PANEL, Note, PanelRow } from './FindPanel';
import FindResults from './FindResults';
import { estimateBody } from './findCost';
import { checkDrawnArea } from './findGeometry';
import { wordsMode } from './findSegment';
import SavedFinds from './SavedFinds';
import HeatmapRow from './SimilarityControls';
import { useFindEstimate } from './useFindEstimate';
import { useFindResults } from './useFindResults';
import { formatArea, polygonM2, useSearchGrid } from './useSearchGrid';
import { MAX_OBJECTS, SIMILARITY_STATUS, useSimilarityMap } from './useSimilarityMap';

// Where a search runs: the squares whose centre is on screen, or a drawn area
const AREAS = [
	{ value: 'view', label: 'This view' },
	{ value: 'drawn', label: 'Draw' },
];

// Typing pauses this long before the squares are sized for the words (each new word asks Claude
// for its size once)
const TEXT_SETTLE_MS = 800;
// Typing pauses this long before the box asks which of our models fit the words (that asks no
// model and no LLM)
const ROUTE_SETTLE_MS = 200;
// A pick from the box's list whose fit is by name only replaces what was typed ("tree f")
const NAME_FITS = ['name', 'prefix'];

// Why the next search cannot run, on the map where the problem is: bottom centre of the map left
// of the panel
const MapMessage = styled.div`
	position: absolute;
	left: calc(50% - ${(PANEL.width + PANEL.right) / 2}px);
	bottom: ${({ theme }) => theme.token.sizeXXL * 2}px;
	transform: translateX(-50%);
	z-index: 5;
	max-width: 60%;
	padding: ${({ theme }) => `${theme.token.paddingSM}px ${theme.token.paddingLG}px`};
	border-radius: ${({ theme }) => theme.token.borderRadiusLG * 1.5}px;
	background: ${({ theme }) => theme.token.colorError};
	color: ${({ theme }) => theme.token.colorWhite};
	font-family: ${({ theme }) => theme.token.fontFamily};
	font-size: ${({ theme }) => theme.token.fontSize}px;
	font-weight: ${({ theme }) => theme.token.fontWeightStrong};
	box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
	pointer-events: none;
`;

// Keys typed into a box (the search, an object's name, a list) are not for the drawing
const isTyping = target =>
	Boolean(
		target?.isContentEditable ||
			target?.closest?.('input, textarea, [contenteditable="true"], .ant-select')
	);

// The backend's "this area needs 425 tiles (limit 400): …" in the panel's words
const areaMessage = error =>
	capitalizeFirst(error.replace(/\btiles\b/g, 'squares').replace('(limit', '(most is'));

// A typed sentence rather than a list of words: Claude reads it first ("find all cars and houses",
// "finn alle biler og hus"); "car" or "car, house" search as they are
const isSentence = typed => {
	const words = typed.split(/[\s,]+/).filter(Boolean);
	return words.length >= 3 || /\s(and|og|og så|with|med)\s/i.test(` ${typed} `);
};

/**
 * "Find" (proof of concept): look for a word, or for objects like examples marked on the map, in
 * the squares on screen or a drawn area; review the squares; outline what was found. The heatmap
 * of the examples covers the whole orthophoto.
 */
const FindSimilar = () => {
	const { project, mapObject, toolBarVisible, isDemo } = useProject();
	const { addToast } = useToast();
	// Only administrators may start compute; for them it starts when the panel opens
	const { roleAdmin } = useAuth();
	const { token } = theme.useToken();
	const [open, setOpen] = useState(false);
	const [text, setText] = useState('');
	// What Claude read a typed sentence as ({ text: "car, house", objects, source }), and while it reads
	const [understood, setUnderstood] = useState(null);
	const [parsing, setParsing] = useState(false);
	// Where searches run: a drawn polygon ({ bounds, polygon }, EPSG:3857), or null for the view
	const [area, setArea] = useState(null);
	const [drawingArea, setDrawingArea] = useState(false);
	// Why the area just drawn was not taken ("The area crosses itself; draw it again.")
	const [areaNote, setAreaNote] = useState(null);
	// The collection Find works for ({ collection, step, ran }): its objects are searched, one search
	// after another (roads apart from objects), and the results save into it
	const [target, setTarget] = useState(null);
	// Start pressed before compute was up: the search runs once it is
	const [startPending, setStartPending] = useState(false);
	const viewCollection = useViewCollection(project?.uuid);

	const orthophoto = project?.map_layers?.find(layer => layer.layer === 'orthophoto');
	// Similarity tiles follow the orthophoto's tile pyramid
	const zoomRange = useMemo(() => {
		const [min = 10, max = 21] = orthophoto?.zoom_range?.split('-').map(Number) ?? [];
		return { min, max };
	}, [orthophoto?.zoom_range]);

	const showError = useCallback(
		error =>
			addToast({
				id: 'find-error',
				bg: 'danger',
				title: 'Find failed',
				message: error?.message ?? 'Unknown error.',
			}),
		[addToast]
	);

	const similarity = useSimilarityMap({
		mapObject,
		projectUuid: project?.uuid,
		zoomRange,
		token,
		onError: showError,
	});
	const isMarking = similarity.status === SIMILARITY_STATUS.MARKING;

	// What the project holds ("tree", "car", "boat"), asked once per project; a click puts the word in
	// the search (needs no compute)
	const projectObjects = useQuery({
		queryKey: ['find', 'objects', project?.uuid],
		queryFn: () => getFindObjects({ projectUuid: project.uuid }),
		enabled: Boolean(open && project?.uuid),
		staleTime: Infinity,
		retry: false,
	});
	// The trained models the user may run here, and the one picked from the box's list
	const models = useQuery({
		queryKey: ['find', 'models', project?.uuid],
		queryFn: () => getFindModels({ projectUuid: project.uuid }),
		enabled: Boolean(open && project?.uuid),
		staleTime: Infinity,
		retry: false,
	});
	const [modelUuid, setModelUuid] = useState(null);
	// The generic search was picked (or our model let go of) for the words typed
	const [genericChosen, setGenericChosen] = useState(false);
	const pickedModel = models.data?.models?.find(m => m.model_uuid === modelUuid) ?? null;
	// Which of our models fit the words typed, asked once typing pauses
	const [routeText, setRouteText] = useState('');
	useEffect(() => {
		const timer = setTimeout(() => setRouteText(text.trim()), ROUTE_SETTLE_MS);
		return () => clearTimeout(timer);
	}, [text]);
	const queryClient = useQueryClient();
	const routeQuery = typed => ({
		queryKey: ['find', 'route', project?.uuid, typed],
		queryFn: () => findRoute({ projectUuid: project.uuid, text: typed }),
		staleTime: 60000,
		retry: false,
	});
	const route = useQuery({
		...routeQuery(routeText),
		enabled: Boolean(open && project?.uuid && routeText),
		placeholderData: keepPreviousData,
	});
	const freshRoute = route.data?.text === text.trim() ? route.data : null;
	// Our model first: the one that fits every word ("trees": Tree Finder) runs instead of the generic
	// finders, unless the generic search was picked
	const fittingModel = freshRoute?.top === 'model' ? freshRoute.models[0] : null;
	const chosenModel = pickedModel ?? (genericChosen ? null : fittingModel);
	const typedWords = isSentence(text)
		? []
		: text
				.split(',')
				.map(word => word.trim())
				.filter(Boolean);
	const pickedObjects = new Set(typedWords.map(word => word.toLowerCase()));
	const pickObject = name => {
		const next = pickedObjects.has(name)
			? typedWords.filter(word => word.toLowerCase() !== name)
			: [...typedWords, name].slice(-MAX_OBJECTS);
		changeText(next.join(', '));
	};
	// New words: what runs is decided for them afresh
	const changeText = value => {
		setText(value);
		setModelUuid(null);
		setGenericChosen(false);
		if (understood && value !== understood.text) setUnderstood(null);
	};
	// A pick from the box's list. A model fitting the words by name only ("tree f") replaces them.
	const chooseModel = uuid => {
		const entry = freshRoute?.models.find(m => m.model_uuid === uuid);
		if (text.trim() && (!entry || NAME_FITS.includes(entry.fit))) setText('');
		setModelUuid(uuid);
		setGenericChosen(false);
		find.clear();
	};
	const chooseGeneric = () => {
		setModelUuid(null);
		setGenericChosen(true);
	};

	// What the next search looks for, so its squares are about ten of them wide: the examples marked,
	// what Claude read a sentence as, or the words typed once typing pauses
	const [settledText, setSettledText] = useState('');
	useEffect(() => {
		const timer = setTimeout(() => setSettledText(text.trim()), TEXT_SETTLE_MS);
		return () => clearTimeout(timer);
	}, [text]);
	const markedBoxes = similarity.groups
		.filter(g => g.boxes.length)
		.map(g => ({ boxes: g.boxes }));
	let sizedFor = null;
	if (markedBoxes.length) sizedFor = { groups: markedBoxes };
	// A model has squares of its own; the area's are unsized meanwhile
	else if (chosenModel) sizedFor = null;
	else if (understood?.text === settledText) sizedFor = { objects: understood.objects };
	else if (settledText && !isSentence(settledText))
		sizedFor = {
			objects: settledText
				.split(',')
				.map(word => word.trim())
				.filter(Boolean),
		};

	// With the heatmap showing, a click on the map adds the object under it to the active object's
	// results, labelled as "find all like these" labels them
	const heatmapReady = similarity.heatmapVisible && similarity.status === SIMILARITY_STATUS.READY;
	const markedCount = similarity.groups.filter(g => g.boxes.length).length;
	const activeGroup = similarity.groups[similarity.activeGroup];
	const activeName = activeGroup?.name?.trim();
	// The largest of its examples, [width, height] in map units: how big a clicked object may be
	const activeBoxes = activeGroup?.boxes;
	const exampleSize = useMemo(
		() =>
			activeBoxes?.length
				? [
						Math.max(...activeBoxes.map(b => b[2] - b[0])),
						Math.max(...activeBoxes.map(b => b[3] - b[1])),
					]
				: null,
		[activeBoxes]
	);
	const pickOnClick = useMemo(
		() =>
			heatmapReady
				? {
						group: similarity.activeGroup,
						label:
							activeName ||
							(markedCount > 1
								? `object ${similarity.activeGroup + 1}`
								: 'like the example'),
						size: exampleSize,
					}
				: null,
		[heatmapReady, similarity.activeGroup, activeName, markedCount, exampleSize]
	);

	// The search grid gives where a search runs; the results give the grid its squares' states
	const areaRef = useRef(() => null);
	const find = useFindResults({
		mapObject,
		projectUuid: project?.uuid,
		token,
		open,
		active: open && !isMarking && !drawingArea,
		pickOnClick,
		searchArea: () => areaRef.current(),
		onError: showError,
		autoStart: Boolean(roleAdmin),
	});
	const grid = useSearchGrid({
		mapObject,
		projectUuid: project?.uuid,
		token,
		enabled: open && !isMarking,
		area,
		search: find.search,
		tileReview: find.tileReview,
		selectedTile: find.selectedTile,
		sizedFor,
	});
	areaRef.current = grid.searchArea;

	// A search picked up from the server (Find opened again while it ran on): show what it looks for,
	// and where (the area it was drawn as)
	const pickedUp = find.search?.request;
	const pickedUpKind = find.search?.kind;
	useEffect(() => {
		if (['query', 'segment'].includes(pickedUpKind) && pickedUp?.text)
			setText(current => current || pickedUp.text);
		if (pickedUpKind === 'model' && pickedUp?.model_uuid) {
			setModelUuid(current => current ?? pickedUp.model_uuid);
		}
		const polygon = pickedUp?.polygon;
		if (polygon?.length) {
			setArea(current => current ?? { bounds: new Polygon([polygon]).getExtent(), polygon });
		}
	}, [pickedUp, pickedUpKind]);

	// Drawing a search area: a polygon, a click per corner and a double-click (or a click on the first
	// corner) to finish; Escape cancels. Its size on the ground shows at its top-right corner while it
	// is drawn. The whole squares it touches run; only what is inside it is kept.
	useEffect(() => {
		if (!mapObject || !drawingArea) return undefined;
		const font = `${token.fontWeightStrong} ${token.fontSize}px ${token.fontFamily}`;
		const sketchStyle = feature => {
			const geometry = feature.getGeometry();
			const type = geometry.getType();
			if (type === 'Point') {
				return new Style({
					image: new CircleStyle({
						radius: 5,
						fill: new Fill({ color: token.colorWhite }),
						stroke: new Stroke({ color: token.colorBgMask, width: 2 }),
					}),
				});
			}
			if (type === 'LineString') {
				return new Style({ stroke: new Stroke({ color: token.colorWhite, width: 2 }) });
			}
			const [, , maxX, maxY] = geometry.getExtent();
			const ring = geometry.getCoordinates()[0];
			return [
				new Style({
					stroke: new Stroke({ color: token.colorWhite, width: 2 }),
					fill: new Fill({ color: withAlpha(token.colorWhite, 0.12) }),
				}),
				new Style({
					geometry: new Point([maxX, maxY]),
					text: new Text({
						text: ring.length > 3 ? formatArea(polygonM2(ring)) : '',
						font,
						textAlign: 'right',
						textBaseline: 'bottom',
						offsetY: -6,
						fill: new Fill({ color: token.colorWhite }),
						stroke: new Stroke({ color: token.colorBgMask, width: 3 }),
					}),
				}),
			];
		};
		const source = new VectorSource();
		const layer = new VectorLayer({
			source,
			zIndex: SIMILARITY_Z_INDEX + 2,
			style: sketchStyle,
		});
		const draw = new Draw({ source, type: 'Polygon', style: sketchStyle });
		// Simplified to the most points the backend takes; one crossing itself, or too small, is not
		// taken: the note says why, and drawing goes on
		draw.on('drawend', ({ feature }) => {
			const checked = checkDrawnArea(feature.getGeometry().getCoordinates()[0]);
			if (checked.note) {
				setAreaNote(checked.note);
				// Draw adds the sketch to the source after this event
				source.once('addfeature', ({ feature: added }) => source.removeFeature(added));
				return;
			}
			setAreaNote(null);
			setArea({ bounds: checked.bounds, polygon: checked.polygon });
			setDrawingArea(false);
		});
		const onKey = event => {
			if (isTyping(event.target)) return;
			if (event.key === 'Escape') setDrawingArea(false);
			if (event.key === 'Backspace') draw.removeLastPoint();
		};
		window.addEventListener('keydown', onKey);
		mapObject.addLayer(layer);
		mapObject.addInteraction(draw);
		mapObject.getViewport().style.cursor = 'crosshair';
		return () => {
			window.removeEventListener('keydown', onKey);
			mapObject.removeInteraction(draw);
			mapObject.removeLayer(layer);
			mapObject.getViewport().style.cursor = '';
		};
	}, [mapObject, drawingArea, token]);

	const startMarking = () => {
		find.clear();
		similarity.start();
	};

	// Another example of a different size (a van after a car): kept with the ones marked before
	const addExample = group => {
		find.clear();
		similarity.addExample(group);
	};

	// The first example of another object ("and houses"), searched with its own examples
	const addObject = () => {
		find.clear();
		similarity.addObject();
	};

	// Closing Find lets go of the search, which runs on: opening Find again picks it up
	const close = () => {
		similarity.close();
		find.detach();
		setText('');
		setModelUuid(null);
		setGenericChosen(false);
		setArea(null);
		setAreaNote(null);
		setDrawingArea(false);
		setTarget(null);
		setStartPending(false);
		setOpen(false);
	};

	// Results, outlines, the examples and the model chosen go; the area and the panel stay
	const clearAll = () => {
		similarity.close();
		find.clear();
		setText('');
		setModelUuid(null);
		setGenericChosen(false);
		setTarget(null);
	};

	// A collection chosen: its first search goes in the box, and Find asks to start it
	const putStep = (collection, step) => {
		similarity.close();
		find.clear();
		// Our model runs when it fits the words, as for words typed
		changeText(collectionStep({ collection, step }).search?.text ?? '');
		setTarget({ collection, step, ran: false });
	};
	const chooseCollection = collection => {
		if (target?.collection.uuid === collection.uuid && !target.ran) return;
		putStep(collection, 0);
	};
	const canClear = Boolean(
		find.search ||
			find.outline ||
			find.outlining ||
			text ||
			modelUuid ||
			similarity.status !== SIMILARITY_STATUS.IDLE
	);

	// The objects marked on the map, each with its examples and name
	const markedGroups = similarity.groups.filter(g => g.boxes.length);
	const exampleCount = markedGroups.reduce((n, g) => n + g.boxes.length, 0);
	const nextArea = grid.preview;
	// Only an area the search would refuse too stops Find; any other failure of the preview is a note,
	// asked again when the map moves (a drawn area: once more, shortly)
	const areaError =
		!drawingArea && nextArea?.error && nextArea.blocking ? areaMessage(nextArea.error) : null;
	const previewFailed = Boolean(!drawingArea && nextArea?.error && !nextArea.blocking);
	const running = Boolean(find.search?.running);
	// Compute's state for Find: a local backend is always ready
	const worker = find.session?.backend === 'worker' ? find.session.state : 'ready';

	// What the next search would cost: the search the Find button would run, over the next area
	const nextBounds = area ?? (nextArea?.searched_bounds && { bounds: nextArea.searched_bounds });
	const nextWords =
		understood?.text === text.trim() ? understood.objects : isSentence(text) ? [] : typedWords;
	// Words naming only roads, rivers, lakes… are traced (segment mode); with objects too, refused
	const nextMode = exampleCount || chosenModel ? null : wordsMode(nextWords);
	const nextKind = exampleCount
		? 'similar'
		: chosenModel
			? 'model'
			: nextMode === 'segment'
				? 'segment'
				: 'query';
	const estimate = useFindEstimate({
		projectUuid: project?.uuid,
		enabled: open && !running && !drawingArea && !areaError,
		body: nextBounds
			? estimateBody({
					kind: nextKind,
					...nextBounds,
					maxTiles: MAX_SEARCH_TILES,
					objects: nextWords,
					groups: markedGroups,
					modelUuid: chosenModel?.model_uuid,
				})
			: null,
		worker,
	});

	// One Find at a time: Enter and a click, or a double click, start one search
	const submittingRef = useRef(false);
	const runFind = async () => {
		if (submittingRef.current) return;
		submittingRef.current = true;
		try {
			await submitFind();
		} finally {
			submittingRef.current = false;
		}
	};
	const submitFind = async () => {
		const name = text.trim() || undefined;
		if (areaError || running || parsing) return;
		// Enter before compute is ready does what the button shows: start compute, or wait for it
		if (worker !== 'ready') {
			if (worker !== 'starting' && !find.startQueued) find.startSession();
			return;
		}
		if (exampleCount) {
			const groups = markedGroups.map(g => ({
				boxes: g.boxes,
				name: g.name.trim() || undefined,
			}));
			find.similar.mutateAsync({ groups }).catch(() => null);
			return;
		}
		let model = chosenModel;
		// Find pressed before the box heard which of our models fit the words: ask now, so ours still
		// come first
		if (!model && name && !genericChosen && !freshRoute) {
			const fresh = await queryClient.fetchQuery(routeQuery(name)).catch(() => null);
			if (fresh?.top === 'model') [model] = fresh.models;
		}
		if (model) {
			find.model.mutate(model.model_uuid);
			return;
		}
		if (!name) return;
		// A sentence ("Find all cars and houses here") is read by Claude first; what it understood goes
		// back into the box ("car, house"), to check or edit, and the next Find runs it
		if (isSentence(name) && understood?.text !== name) {
			setParsing(true);
			try {
				const { objects, source } = await findParse({
					projectUuid: project.uuid,
					text: name,
				});
				const words = objects.join(', ');
				setText(words);
				setUnderstood({ text: words, objects, source });
			} catch (error) {
				showError(error);
			} finally {
				setParsing(false);
			}
			return;
		}
		find.query.mutate(name);
	};

	// The collection's search ran once Find started it (or its results are on the map)
	const startCollectionSearch = async () => {
		if (worker !== 'ready') {
			setStartPending(true);
			if (worker !== 'starting' && !find.startQueued) find.startSession();
			return;
		}
		await runFind();
	};
	useEffect(() => {
		if (!startPending) return;
		if (!target || worker === 'failed') {
			setStartPending(false);
			return;
		}
		if (worker === 'ready') {
			setStartPending(false);
			runFind();
		}
		// runFind reads this render's state; it is called once, when compute is up
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startPending, worker, target]);
	const searchRunUuid = find.runUuid;
	useEffect(() => {
		if (searchRunUuid && target && !target.ran && find.search?.request?.text === text.trim())
			setTarget(current => (current ? { ...current, ran: true } : current));
	}, [searchRunUuid, target, find.search?.request?.text, text]);

	if (!orthophoto) return null;

	if (!open) {
		return (
			<Tooltip title={isDemo ? 'Not available in demo projects' : 'Find objects'}>
				<span>
					<Button
						icon={<IoSparklesOutline />}
						onClick={() => setOpen(true)}
						disabled={!toolBarVisible || isDemo}>
						Find
					</Button>
				</span>
			</Tooltip>
		);
	}

	return (
		<>
			<Button icon={<IoCloseCircleOutline />} onClick={close}>
				Close Find
			</Button>
			{/* Lines up a click with the centre of a small object */}
			<AnnotationCrosshair map={mapObject} active={isMarking} />
			{areaError &&
				!running &&
				mapObject &&
				createPortal(
					<MapMessage role="alert">{areaError}</MapMessage>,
					mapObject.getViewport()
				)}
			<FindPanel
				session={find.session}
				startRequestedAt={find.startRequestedAt}
				onStart={find.startSession}
				onClear={clearAll}
				canClear={canClear}
				onClose={close}>
				<LookingFor
					text={text}
					onTextChange={changeText}
					understood={
						understood?.source === 'claude' && understood.text === text
							? understood
							: null
					}
					parsing={parsing}
					onSubmit={runFind}
					route={route.data}
					models={models.data?.models ?? []}
					model={chosenModel}
					modelPicked={Boolean(pickedModel)}
					genericOver={genericChosen ? fittingModel : null}
					onChooseModel={chooseModel}
					onChooseGeneric={chooseGeneric}
					onReleaseModel={chooseGeneric}
					groups={similarity.groups}
					activeGroup={similarity.activeGroup}
					marking={isMarking}
					onMark={startMarking}
					onAddExample={addExample}
					onAddObject={addObject}
					onGroupName={similarity.setGroupName}
					onCancelMarking={similarity.cancelMarking}
					markDisabled={drawingArea}
					projectObjects={projectObjects}
					pickedObjects={pickedObjects}
					onPickObject={pickObject}
					wordsMode={nextMode}
				/>
				{target && (
					<CollectionAsk
						target={target}
						where={area ? 'the drawn area' : 'this view'}
						ran={target.ran}
						busy={running || parsing || Boolean(areaError)}
						computeReady={worker === 'ready'}
						queued={startPending}
						onStart={startCollectionSearch}
						onNext={() => putStep(target.collection, target.step + 1)}
						onCancel={() => setTarget(null)}
					/>
				)}
				<PanelRow label="Area">
					<Segmented
						block
						value={area || drawingArea ? 'drawn' : 'view'}
						onChange={value => {
							setAreaNote(null);
							if (value === 'drawn') {
								setDrawingArea(true);
							} else {
								setArea(null);
								setDrawingArea(false);
							}
						}}
						// No drawing while an example is being marked: both take the map's clicks
						options={AREAS.map(option =>
							option.value === 'drawn' ? { ...option, disabled: isMarking } : option
						)}
					/>
					{drawingArea && (
						<Note>
							Click the corners of the area on the map; double-click to finish. Esc
							cancels.
						</Note>
					)}
					{areaNote && <Note role="alert">{areaNote}</Note>}
					{previewFailed && !running && (
						<Note>
							The squares could not be shown just now; Find still searches here.
						</Note>
					)}
				</PanelRow>
				<FindButton
					text={text}
					sentence={isSentence(text.trim()) && understood?.text !== text.trim()}
					parsing={parsing}
					groups={similarity.groups}
					running={running}
					blockedByArea={Boolean(areaError)}
					worker={worker}
					startQueued={find.startQueued}
					modelName={chosenModel?.name}
					estimate={estimate?.estimate}
					onStartWorker={find.startSession}
					onFind={runFind}
				/>
				{exampleCount > 0 && <HeatmapRow {...similarity} />}
				<FindResults
					find={find}
					colors={token.similarityColormap}
					saveTo={target?.collection ?? null}
					onViewCollection={viewCollection}
				/>
				<FindCollections
					projectUuid={project?.uuid}
					open={open}
					chosenUuid={target?.collection.uuid ?? null}
					onFind={chooseCollection}
				/>
				<SavedFinds mapObject={mapObject} projectUuid={project?.uuid} open={open} />
			</FindPanel>
		</>
	);
};

export default FindSimilar;
